import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import CookieJs from "js-cookie";
import { get } from "lodash";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import * as yup from "yup";
import useNotification from "../hooks/useNotification";
import groupService from "../services/group";
import queryDefault from "../utils/queryDefault";

type Props = {
  open: boolean;
  id: number | null;
  onClose: () => void;
  onSaved?: () => void;
};

const schema = yup.object().shape({
  name: yup.string().required("group name is a required field"),
});

export default function BranchGroupEditModal({ open, id, onClose, onSaved }: Props) {
  const notification = useNotification();
  const user = JSON.parse(CookieJs.get("USER") || "{}");

  const currentGroup = useQuery(
    ["view-group", id],
    () => groupService.getDetailBranchGroup(id),
    {
      ...queryDefault,
      enabled: Boolean(open && id),
    }
  );

  const form = useForm({
    defaultValues: {
      name: "",
      username: user.username || "",
      percentage: 0,
      region: "",
      category: "",
      kode_cabang: "",
      bep: 0,
    },
    resolver: yupResolver(schema),
  });

  const updateBranchGroup = useMutation("update-group-modal", groupService.updateBranchGroup, {
    onSuccess: (response: any) => {
      notification.onOpen({
        message: response.message === "" ? "Group was successfully updated!" : response.message,
        type: "success",
        position: "top",
      });
      if (onSaved) {
        onSaved();
      }
      onClose();
    },
    onError: (error: any) => {
      notification.onOpen({
        message:
          error?.response?.data?.message === undefined
            ? "Something went wrong"
            : error?.response?.data?.message,
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onSubmit = (values: any) => {
    if (!id) {
      return;
    }

    updateBranchGroup.mutate({
      data: {
        ...values,
        id,
      },
    });
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    if (currentGroup.data?.data) {
      form.setValue("name", get(currentGroup, "data.data.name") || "");
      form.setValue("percentage", get(currentGroup, "data.data.percentage") ?? 0);
      form.setValue("region", get(currentGroup, "data.data.region") || "");
      form.setValue("category", get(currentGroup, "data.data.category") || "");
      form.setValue("kode_cabang", get(currentGroup, "data.data.kode_cabang") || "");
      form.setValue("bep", get(currentGroup, "data.data.bep") ?? 0);
      form.setValue("username", user.username || "");
    }
  }, [currentGroup.data, form, open, user.username]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Branch Group</DialogTitle>
      <DialogContent>
        {!currentGroup.isLoading ? (
          <Box sx={{ mt: 1 }}>
            <Grid container>
              <Grid item lg={12} xs={12}>
                <FormControl fullWidth margin="normal">
                  <FormLabel required>Group Name</FormLabel>
                  <Controller
                    name="name"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <FormLabel required>Percentage</FormLabel>
                  <Controller
                    name="percentage"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="number"
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <FormLabel required>Region</FormLabel>
                  <Controller
                    name="region"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <FormLabel required>Category</FormLabel>
                  <Controller
                    name="category"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        select
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      >
                        <MenuItem value="">Select category</MenuItem>
                        <MenuItem value="small">Small</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="mega">Mega</MenuItem>
                      </TextField>
                    )}
                  />
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <FormLabel required>Kode Cabang</FormLabel>
                  <Controller
                    name="kode_cabang"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <FormLabel required>BEP</FormLabel>
                  <Controller
                    name="bep"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="number"
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={form.handleSubmit(onSubmit)}
          loading={updateBranchGroup.isLoading}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
